import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sidebar } from "primereact/sidebar";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import Form from "../../shared/components/Form/Form";
import ProcessingModal from "../../shared/components/Form/EvidenceModals";
import { handleRedirectBack } from "../../shared/utils/navigationUtils";
import { getQuestions } from "./identification-form";
import { identificationService } from "../../services";
import { UserInterfaces } from "../../interfaces";
import { countryLookup } from "../../shared/data/countries";
import styles from "./identification.module.scss";

interface EvidenceDetails {
  evidenceURL: string;
  fileName: string;
}

function Identification2() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  const [formDetails, setFormDetails] = useState<any>({
    onFormSubmit: undefined,
    stepsConfig: [
      {
        label: translate("preview.form.step1"),
      },
    ],
    submitLabel: translate("preview.form.submit"),
    name: translate("preview.header"),
    fields: [],
  });

  const [state, setState] = useState("loading");
  const [currentWidth] = useState(window.innerWidth);

  const [evidenceDetails, setEvidenceDetails] =
    useState<EvidenceDetails>();

  const [consentChecked, setConsentChecked] =
    useState(false);
  
  const handleConsentChange = (
    event: CheckboxChangeEvent,
  ) => {
    setConsentChecked(event.checked ?? false);
  };

  const [showPreview, setShowPreview] =
    useState(false);

  const [error, setError] = useState<
    Error | string | null
  >(null);

  const widthCutOff = 768;

  const [selectedCountry, setSelectedCountry] = useState({
    label: translate("country.ireland"),
    flagUrl: "https://flagcdn.com/w80/ie.png",
  });

  /*
   * The cleanup runs whenever the preview URL changes and when the component
   * unmounts. It prevents generated object URLs from leaking browser memory.
   */
  useEffect(() => {
    const previewUrl = evidenceDetails?.evidenceURL;

    return () => {
      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [evidenceDetails?.evidenceURL]);

  const requestDocument = useCallback(
    async (data: any) => {
      try {
        setState("loading-docs");
        setError(null);
        setConsentChecked(false);
        setShowPreview(false);

        await identificationService.checkAuthorizationStatus(
          data,
        );

        const previewDetails =
          await identificationService.getWatermarkedPreview();

        setEvidenceDetails(previewDetails);
        setState("success");
      } catch (caughtError: unknown) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : translate("form.error.message"),
        );

        setState("Error");
      }
    },
    [translate],
  );

  async function shareDoc() {
    setState("transferring-docs");
    setShowPreview(false);

    try {
      const response =
        await identificationService.acceptDoc();

      if (!response.ok) {
        throw new Error(
          translate("preview.error.acceptFailed"),
        );
      }

      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 1500);
      });
      
      await handleRedirectBack(navigate);
    } catch (caughtError: unknown) {
      window.setTimeout(() => {
        setError(
          caughtError instanceof Error
            ? caughtError
            : translate(
                "preview.error.acceptRequestFailed",
              ),
        );

        setState("Error");
      }, 1500);
    }
  }

  const fetchData = useCallback(async () => {
    try {
      const formQuestions = getQuestions();
      const [claims] = await Promise.all([
        identificationService.getUserClaimsFromServer(),
        identificationService.getToken(),
      ]);

      claims.birthDate = claims.birthDate
        ? new Date(claims.birthDate)
        : undefined;

      const countryCode = claims.ppsn?.slice(0, 2).toUpperCase();
      const country = countryCode ? countryLookup[countryCode] : undefined;

      if (country) {
        const flagCode = countryCode === "EL" ? "gr" : countryCode.toLowerCase();
        setSelectedCountry({
          label: translate(country.nameKey),
          flagUrl: `https://flagcdn.com/w80/${flagCode}.png`,
        });
      }

      const isEidas = ((claims as any).method ?? null) === "EIDAS";

      Object.keys(claims).forEach((key) => {
        const question: any = formQuestions.find((formQuestion) => formQuestion.name === key);

        if (question) {
          question.value = claims[key as keyof UserInterfaces["UserData"]];
        }

        if (question?.name === "email" && isEidas) {
          question.optional = true;
        }
      });

      setFormDetails((previousDetails: any) => ({
        ...previousDetails,
        fields: formQuestions,
        disableSubmit: (getValues: Function, _step: number,) => !getValues("consent"),
        hideFooter: (getValues: Function, step: number) => step === 2 && (currentWidth < widthCutOff || !getValues("providerCountry")?.code),
        showSidebar: (getValues: Function, step: number) => step === 2 && Boolean(getValues("documents")) && currentWidth < widthCutOff,
        onFormSubmit: requestDocument,
      }));

      setState("ready");
    } catch (caughtError: unknown) {
      setError(
        caughtError instanceof Error
          ? caughtError
          : translate("form.error.message"),
      );

      setState("Error");
    }
  }, [currentWidth, requestDocument, translate]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const handleReject = () => {
    setShowPreview(false);
    setConsentChecked(false);
    setState("ready");
  };

  const handleCloseError = () => {
    setError(null);
    setState("ready");
  };

  const renderRetrievedDoc = () => (
    <>
      <h2>{translate("preview.document.heading")}</h2>

      <div className="mt-5">
        {evidenceDetails && (
          <div
            key={evidenceDetails.fileName}
            className="grid"
          >
            <div className="col-1 md:col-2 mb-3" />

            <span
              id="TXT_PREVIEW_FILENAME"
              className="col-6 md:col-4"
              style={{
                border: "black 1px solid",
                background: "var(--gray-100)",
                fontWeight: 600,
                paddingTop: "var(--space-sixteen)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {evidenceDetails.fileName}
            </span>

            <div
              style={{
                border: "black 1px solid",
                padding: "var(--space-base)",
                paddingTop: "7px",
              }}
            >
              <Button
                id="BTN_PREVIEW"
                label={translate(
                  "preview.button.preview",
                )}
                severity="secondary"
                onClick={() => setShowPreview(true)}
                disabled={
                  !evidenceDetails.evidenceURL
                }
              />
            </div>
          </div>
        )}
      </div>

      <div className="grid mt-5">
        <div className="col-1 md:col-2" />

        <div className="col-10">
          <Checkbox
            inputId="CHK_CONSENT"
            checked={consentChecked}
            onChange={handleConsentChange}
          />

          <label htmlFor="CHK_CONSENT">
            {translate("preview.form.consent")}
          </label>

          <div className="col-12">
            <span>
              {translate("preview.form.contact")}
            </span>

            <span>
              <a
                href="mailto:Help@INPS.gov.ie"
                target="_blank"
                rel="noopener noreferrer"
              >
                Help@INPS.gov.ie
              </a>
            </span>
          </div>

          <div
            className={`col-12 md:col-8 ${
              styles["sidepanel-container"]
            }`}
          >
            <Button
              id="BTN_SUBMIT"
              className="mb-3"
              disabled={!consentChecked}
              label={translate(
                "preview.form.submit",
              )}
              severity="secondary"
              onClick={() => {
                void shareDoc();
              }}
            />

            <Button
              id="BTN_REJECT"
              className="mb-3"
              label={translate("form.reject")}
              onClick={handleReject}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderError = () => (
    <>
      <div className={styles["error-container"]}>
        <i
          className={`pi pi-exclamation-triangle ${
            styles["error-icon"]
          }`}
        />

        <h2 className={styles["error-title"]}>
          {translate("error.default.message")}
        </h2>
      </div>

      <div
        className={`m-2 ${
          styles["error-body-container"]
        }`}
      >
        <strong>
          {error instanceof Error
            ? translate(error.message)
            : error || translate("form.error.message")}
        </strong>

        <p>
          {translate("form.error.persistent")}
        </p>
      </div>

      <div
        className={`col-12 md:col-8 ${
          styles["sidepanel-container"]
        }`}
      >
        <Button
          id="BTN_CLOSE"
          className="mb-3"
          label={translate(
            "preview.button.close",
          )}
          onClick={handleCloseError}
        />
      </div>
    </>
  );

  if (state === "loading") {
    return (
      <div className="loading-mask">
        <ProgressSpinner className="spinner" />
      </div>
    );
  }

  return (
    <>
      <h1>{translate("preview.header")}</h1>

      {state === "loading-docs" && (
        <div className="loading-mask">
          <ProgressSpinner className="spinner" />
        </div>
      )}

      {state === "transferring-docs" && (
        <ProcessingModal
          previewChecked
          errorMessage=""
          selectedCountry={selectedCountry}
        />
      )}

      <Form
        onFormSubmit={formDetails.onFormSubmit}
        stepsConfig={formDetails.stepsConfig}
        fields={formDetails.fields}
        submitLabel={formDetails.submitLabel}
        name={formDetails.name}
        disableSubmit={formDetails.disableSubmit}
        useMeter
        hideFooter={formDetails.hideFooter}
        showSidebar={formDetails.showSidebar}
      />

      <Sidebar
        id="MDL_DOCUMENTS"
        visible={
          state === "success" ||
          state === "Error"
        }
        className="light"
        position="bottom"
        onHide={() => {
          setShowPreview(false);
          setState("ready");
        }}
      >
        {state === "success" &&
          renderRetrievedDoc()}

        {state === "Error" && renderError()}
      </Sidebar>

      <Dialog
        id="DLG_PREVIEW"
        className={`${styles["preview-dialog"]} light`}
        header={evidenceDetails?.fileName}
        data-cy="preview-dialog"
        footer={
          <Button
            id="BTN_CLOSE_PREVIEW"
            data-cy="close-preview-dialog"
            label={translate("preview.button.close")}
            onClick={handleClosePreview}
          />
        }
        visible={
          showPreview &&
          Boolean(evidenceDetails?.evidenceURL)
        }
        modal
        dismissableMask={false}
        onHide={handleClosePreview}
      >
        {evidenceDetails?.evidenceURL && (
          <div
            id="BOX_IMAGE_PREVIEW"
            className={styles["preview-container"]}
            onContextMenu={(event) => {
              event.preventDefault();
            }}
          >
            <img
              id="IMG_WATERMARKED_PREVIEW"
              src={evidenceDetails.evidenceURL}
              alt={translate("preview.modal.imageAlt")}
              className={styles["preview-image"]}
              draggable={false}
            />
          </div>
        )}
      </Dialog>
    </>
  );
}

export default Identification2;